/**
 * Auth API - Type-safe auth endpoints
 * Matches backend: app/api/v1/routes/auth.py
 */

import { apiClient, type ApiError } from './apiClient';
import type { AuthEnvelope, RegisterRequest, LoginRequest, UserResponse, TokenResponse } from '../../types/rest';
import type { AuthErrorCode } from '../../types';

/**
 * Register new user
 * POST /api/v1/auth/register
 * 201 -> AuthEnvelope
 * 409 -> username_taken
 * 422 -> validation error
 */
export async function register(request: RegisterRequest): Promise<AuthEnvelope> {
  const response = await apiClient.post<AuthEnvelope>('/auth/register', request);
  return response.data;
}

/**
 * Login user
 * POST /api/v1/auth/login
 * 200 -> AuthEnvelope
 * 401 -> invalid_credentials
 * 422 -> validation error
 */
export async function login(request: LoginRequest): Promise<AuthEnvelope> {
  const response = await apiClient.post<AuthEnvelope>('/auth/login', request);
  return response.data;
}

/**
 * Refresh access token using refresh token (httpOnly cookie)
 * POST /api/v1/auth/refresh
 * 200 -> TokenResponse
 * 401 -> token_expired / token_invalid
 * 422 -> validation error
 */
export async function refreshToken(): Promise<TokenResponse> {
  const response = await apiClient.post<TokenResponse>('/auth/refresh', {});
  return response.data;
}

/**
 * Get current user profile (requires valid access token)
 * GET /api/v1/auth/me
 * 200 -> UserResponse
 * 401 -> unauthorized
 */
export async function getCurrentUser(): Promise<UserResponse> {
  const response = await apiClient.get<UserResponse>('/auth/me');
  return response.data;
}

/**
 * Logout - invalidate refresh token on server
 * POST /api/v1/auth/logout
 * 200 -> success
 * 401 -> unauthorized
 */
export async function logout(): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>('/auth/logout', {});
  return response.data;
}

/**
 * Change password
 * POST /api/v1/auth/change-password
 * 200 -> success
 * 401 -> unauthorized
 * 422 -> validation error
 */
export async function changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
  const response = await apiClient.post<{ success: boolean }>('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return response.data;
}

/**
 * Delete account
 * DELETE /api/v1/auth/account
 * 200 -> success
 * 401 -> unauthorized
 */
export async function deleteAccount(password: string): Promise<{ success: boolean }> {
  const response = await apiClient.delete<{ success: boolean }>('/auth/account', {
    data: { password },
  });
  return response.data;
}

/**
 * Auth error codes from backend
 */
export const AUTH_ERROR_CODES = {
  INVALID_CREDENTIALS: 'invalid_credentials',
  USERNAME_TAKEN: 'username_taken',
  TOKEN_EXPIRED: 'token_expired',
  TOKEN_INVALID: 'token_invalid',
  REFRESH_EXPIRED: 'refresh_expired',
  REFRESH_INVALID: 'refresh_invalid',
  REFRESH_REVOKED: 'refresh_revoked',
} as const;

/**
 * Check if error is specific auth error code
 */
export function isAuthError(error: unknown, code: AuthErrorCode): boolean {
  if (!error || typeof error !== 'object') return false;
  const apiError = error as ApiError & { errorCode?: string };
  return apiError.errorCode === code;
}

/**
 * Check if error requires re-authentication
 */
export function requiresReauth(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const apiError = error as ApiError;
  return (
    apiError.status === 401 ||
    isAuthError(error, AUTH_ERROR_CODES.TOKEN_EXPIRED) ||
    isAuthError(error, AUTH_ERROR_CODES.TOKEN_INVALID) ||
    isAuthError(error, AUTH_ERROR_CODES.REFRESH_EXPIRED) ||
    isAuthError(error, AUTH_ERROR_CODES.REFRESH_INVALID) ||
    isAuthError(error, AUTH_ERROR_CODES.REFRESH_REVOKED)
  );
}